import { EnvironmentProviders, makeEnvironmentProviders } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { EmulatorConfig } from '@context/shared/types/common';
import { getAnalytics } from 'firebase/analytics';
import { FirebaseOptions, initializeApp } from 'firebase/app';
import {
	connectAuthEmulator,
	getAuth,
	indexedDBLocalPersistence,
	initializeAuth,
} from 'firebase/auth';
import {
	connectFirestoreEmulator,
	initializeFirestore,
} from 'firebase/firestore';
import { connectStorageEmulator, getStorage } from 'firebase/storage';
import {
	FIREBASE_APP,
	FIREBASE_AUTH,
	FIREBASE_STORAGE,
	FIRESTORE,
} from './firebase.tokens';

/**
 * Initializes the Firebase SDK and exposes its singletons (app, firestore, auth,
 * storage) through DI. This replaces the `@angular/fire` provider functions
 * (`provideFirebaseApp`, `provideFirestore`, `provideAuth`, `provideStorage`) so the
 * application can depend on the Firebase SDK directly.
 *
 * On native (Capacitor) platforms auth is initialized with IndexedDB persistence to
 * match the previous behavior.
 *
 * When `emulators` is supplied (the `local` build configuration) the SDK is pointed at
 * the local emulator suite instead of the live project, and Analytics is skipped.
 *
 * @param config the Firebase project configuration (from the app environment)
 * @param emulators optional local emulator connection details
 */
export function provideFirebase(
	config: FirebaseOptions,
	emulators?: EmulatorConfig,
): EnvironmentProviders {
	const app = initializeApp(config);

	const auth = Capacitor.isNativePlatform()
		? initializeAuth(app, { persistence: indexedDBLocalPersistence })
		: getAuth(app);
	// `experimentalAutoDetectLongPolling` lets the SDK fall back from WebChannel
	// streaming to long-polling when the network can't sustain a stream (common on
	// mobile/cellular, VPNs, and restrictive proxies). Without it, reads can hang
	// indefinitely with no error, leaving loading spinners stuck forever.
	const firestore = initializeFirestore(app, {
		experimentalAutoDetectLongPolling: true,
	});
	const storage = getStorage(app);

	if (emulators) {
		connectAuthEmulator(
			auth,
			`http://${emulators.host}:${emulators.authPort}`,
			{ disableWarnings: true },
		);
		connectFirestoreEmulator(
			firestore,
			emulators.host,
			emulators.firestorePort,
		);
		connectStorageEmulator(storage, emulators.host, emulators.storagePort);
	} else {
		// Analytics is best-effort: it is unavailable in some contexts (e.g. native
		// webviews) and throws when unsupported, so we never let it break bootstrap.
		// It is also skipped against emulators since there is no project to report to.
		try {
			getAnalytics(app);
		} catch {
			// analytics not supported in this environment
		}
	}

	return makeEnvironmentProviders([
		{ provide: FIREBASE_APP, useValue: app },
		{ provide: FIRESTORE, useValue: firestore },
		{ provide: FIREBASE_STORAGE, useValue: storage },
		{ provide: FIREBASE_AUTH, useValue: auth },
	]);
}
